/**
 * Utility functions for the dashboard charts and formatting.
 */

/**
 * Calculate linear regression trend line for any numeric data.
 * Skips null/undefined values so the trend only covers months with actual data.
 */
export const calculateTrendLine = <T extends Record<string, unknown>>(
  data: T[],
  valueKey: string,
  trendKey: string = 'trend'
): T[] => {
  if (!data || data.length < 2) return data;

  // Only use data points that have actual values (not null/undefined)
  const validPoints: { index: number; value: number }[] = [];
  data.forEach((d, i) => {
    const val = d[valueKey];
    if (val !== null && val !== undefined) {
      validPoints.push({ index: i, value: Number(val) });
    }
  });

  if (validPoints.length < 2) {
    return data.map(d => ({ ...d, [trendKey]: d[valueKey] !== null && d[valueKey] !== undefined ? Number(d[valueKey]) : null }));
  }

  const n = validPoints.length;
  const xMean = validPoints.reduce((a, p) => a + p.index, 0) / n;
  const yMean = validPoints.reduce((a, p) => a + p.value, 0) / n;

  let numerator = 0;
  let denominator = 0;
  for (const p of validPoints) {
    numerator += (p.index - xMean) * (p.value - yMean);
    denominator += (p.index - xMean) ** 2;
  }

  const slope = denominator !== 0 ? numerator / denominator : 0;
  const intercept = yMean - slope * xMean;

  return data.map((d, i) => ({
    ...d,
    [trendKey]: d[valueKey] !== null && d[valueKey] !== undefined
      ? Math.max(0, intercept + slope * i)
      : null,
  }));
};

/**
 * Calculate dynamic Y-axis domain for percentage charts.
 * Returns [min, max] with padding - scales from the data values and the target
 * (so the target line stays on-chart), capped at 100: a percentage axis never
 * advertises more than 100% (CR-7096). Top-edge headroom for marks near 100 is
 * the YAxis padding prop's job, not the domain's.
 */
export const getPercentageDomain = (dataArray: Array<Record<string, unknown>>, valueKey: string): [number, number] => {
  if (!dataArray || dataArray.length === 0) return [0, 100];

  const values = dataArray.map(d => d[valueKey]).filter((v): v is number => typeof v === 'number' && !isNaN(v));
  const targets = dataArray.map(d => d['target']).filter((v): v is number => typeof v === 'number' && !isNaN(v));
  const allValues = [...values, ...targets];
  if (allValues.length === 0) return [0, 100];

  const minVal = Math.min(...allValues);
  const maxVal = Math.max(...allValues);
  const range = maxVal - minVal;
  const padding = Math.max(range * 0.5, 10);

  const domainMin = Math.max(0, Math.floor((minVal - padding) / 5) * 5);
  const domainMax = Math.min(100, Math.ceil((maxVal + padding) / 5) * 5);

  return [domainMin, domainMax];
};

/**
 * Helper function to parse month string to date range.
 * Supports formats: "Jan", "Jan 24", "Jan 2024"
 */
export const parseMonthToDateRange = (monthStr: string): { date_from: string; date_to: string } | null => {
  try {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const parts = monthStr.trim().split(' ');

    const monthIndex = monthNames.indexOf(parts[0]);
    if (monthIndex === -1) return null;

    let year: number;
    if (parts.length >= 2 && parts[1]) {
      year = parseInt(parts[1]);
      if (year < 100) year += 2000;
    } else {
      year = new Date().getFullYear();
    }

    // CR-4671: Format in local timezone, not UTC. new Date(year, m, 1) is
    // midnight LOCAL time — .toISOString() then converts to UTC and shifts
    // the day back by one for any timezone ahead of UTC (CET, IST, etc.).
    // That was the final cause of the 16 → 15 drill-down mismatch: the
    // user's browser sent date_from=2025-03-31 / date_to=2025-04-29 instead
    // of 2025-04-01 / 2025-04-30, dropping the GRN received on Apr 30.
    const startDate = new Date(year, monthIndex, 1);
    const endDate = new Date(year, monthIndex + 1, 0);
    const fmt = (d: Date): string =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    return {
      date_from: fmt(startDate),
      date_to: fmt(endDate),
    };
  } catch (e) {
    console.error('Error parsing month:', monthStr, e);
    return null;
  }
};
